import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "@/app/rootReducer";
import { useNotification } from "@/context/NotificationContext";
import {
  submitChapterActivityAction,
  resetSubmitState,
} from "@/features/myChapterActivity/slices/chapterActivitySlice";
import {
  ChapterActivityFormData,
  chapterActivityFormSchema,
} from "@/features/myChapterActivity/SubmitChapterActivitySchema";

export function useSubmitChapterActivity() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  // Get submission state from Redux
  const { submitLoading, submitError, submitSuccess } = useSelector(
    (state: RootState) => ({
      submitLoading: state.chapterActivity.submitLoading,
      submitError: state.chapterActivity.submitError,
      submitSuccess: state.chapterActivity.submitSuccess,
    }),
  );

  // Define default values
  const defaultValues = {
    chapterActivityName: "",
    chapterActivityDate: new Date().toISOString().split("T")[0], // Today's date
    activityType: "",
    ofNscsMembers: 0,
    ofNonNscsMembers: 0,
    advisorPresent: false,
    description: "",
    otherCampusOrgsInvolved: "",
  };

  // Initialize the form
  const form = useForm<ChapterActivityFormData>({
    resolver: zodResolver(chapterActivityFormSchema),
    defaultValues,
  });

  // Reset the form and submission state
  const resetForm = useCallback(() => {
    form.reset(defaultValues);
    dispatch(resetSubmitState());
  }, [form, dispatch]);

  // Submit form handler
  const submitForm = useCallback(
    async (values: ChapterActivityFormData) => {
      // In the real implementation, you would need to add the chapter ID
      // from the user's profile or session here before dispatching
      await dispatch(
        submitChapterActivityAction({
          ...values,
        }),
      );
    },
    [dispatch],
  );

  // Handle cancel button click
  const handleCancel = useCallback(() => {
    resetForm();
    navigate("/chapter-activities");
  }, [resetForm, navigate]);

  // Navigate back to the list view after successful submission
  useEffect(() => {
    if (submitSuccess) {
      // Reset form first to ensure it's cleared properly
      resetForm();

      // Show notification with auto-dismiss option (3 seconds)
      showNotification(
        "Success",
        "Your activity has been submitted successfully!",
        "success",
      );

      // Navigate after notification is shown
      const timer = setTimeout(() => {
        navigate("/chapter-activities");
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [submitSuccess, showNotification, navigate, resetForm]);

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetSubmitState());
    };
  }, [dispatch]);

  return {
    form,
    submitLoading,
    submitError,
    submitSuccess,
    submitForm,
    resetForm,
    handleCancel,
  };
}
